import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TermsConditionsContent } from './terms-conditions-content';

interface TermsConditionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'accept' | 'view';
  onAccept?: () => void;
}

const SCROLL_BOTTOM_THRESHOLD_PX = 40;
const RENDER_SETTLE_DELAY_MS = 100;

export function TermsConditionsModal({
  open,
  onOpenChange,
  mode,
  onAccept,
}: TermsConditionsModalProps) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const checkIfScrolledToBottom = useCallback((element: HTMLDivElement) => {
    const { scrollHeight, scrollTop, clientHeight } = element;
    if (scrollHeight - scrollTop - clientHeight < SCROLL_BOTTOM_THRESHOLD_PX) {
      setHasScrolledToBottom(true);
    }
  }, []);

  // Check if content is short enough to not need scrolling
  useEffect(() => {
    if (!open) {
      setHasScrolledToBottom(false);
      return;
    }

    // Use a small delay to ensure the dialog has rendered
    const timer = setTimeout(() => {
      if (scrollRef.current) {
        checkIfScrolledToBottom(scrollRef.current);
      }
    }, RENDER_SETTLE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [open, checkIfScrolledToBottom]);

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    if (!hasScrolledToBottom) {
      checkIfScrolledToBottom(e.currentTarget);
    }
  }

  function handleAccept() {
    onAccept?.();
    onOpenChange(false);
  }

  function handleClose() {
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Terms &amp; Conditions</DialogTitle>
        </DialogHeader>

        <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-1">
          <TermsConditionsContent />
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {mode === 'accept' ? (
            <>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={!hasScrolledToBottom}
                onClick={handleAccept}
                className="bg-[#131313] text-white hover:bg-[#131313]/90"
              >
                Accept
              </Button>
            </>
          ) : (
            <Button type="button" variant="outline" onClick={handleClose}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
